import { Link } from 'react-router-dom';

export default function FlowEfficiencyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
            Flow Efficiency
          </div>
          <h1 className="text-5xl font-bold mb-6 leading-tight">
            忙しいのに成果が出ない薬局の正体
          </h1>
          <p className="text-2xl text-emerald-50 font-light">
            ― 生産性を壊しているのは「待ち」だった ―
          </p>
        </div>
      </section>

      {/* Conclusion */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-500 p-8 rounded-lg shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-2xl text-white"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">結論</h2>
              <p className="text-lg text-gray-800 leading-relaxed">
                多くの調剤薬局では、人はずっと忙しいのに、患者はずっと待っています。その原因は「能力不足」ではなく、<strong className="text-orange-600">待ち時間（滞留）の設計ミス</strong>です。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Theory Explanation */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">理論の解説｜フロー効率とは何か</h2>
        
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-8 rounded-2xl mb-8 border border-emerald-200">
          <p className="text-lg text-gray-800 mb-6">
            フロー効率（Flow Efficiency）とは、
          </p>
          <div className="bg-white p-6 rounded-xl shadow-sm mb-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">
                付加価値時間 ÷ 全リードタイム
              </div>
              <p className="text-sm text-gray-600">で表される指標です。</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white p-5 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-xl text-white"></i>
                </div>
                <h3 className="font-bold text-gray-900">付加価値時間</h3>
              </div>
              <p className="text-gray-700 text-sm">薬を作る、鑑査する、投薬説明する時間</p>
            </div>
            
            <div className="bg-white p-5 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                  <i className="ri-timer-line text-xl text-white"></i>
                </div>
                <h3 className="font-bold text-gray-900">リードタイム</h3>
              </div>
              <p className="text-gray-700 text-sm">受付から薬を受け取るまでの全時間（＝待ちを含む）</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 p-6 rounded-lg">
          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
            <i className="ri-error-warning-line text-red-500"></i>
            驚く事実があります。
          </h3>
          <div className="space-y-2 text-gray-800">
            <p className="text-lg font-semibold text-red-600">
              多くの現場で、付加価値時間は全体の5〜10％しかない
            </p>
            <p className="text-base">
              残りの90％以上は、「次の工程待ち」「置かれているだけの時間」です。
            </p>
          </div>
        </div>
      </section>

      {/* Why Adding People Doesn't Help */}
      <section className="bg-gradient-to-br from-gray-50 to-slate-50 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">なぜ「人を増やしても」楽にならないのか</h2>
          
          <p className="text-lg text-gray-700 mb-8">
            フロー効率の視点がない改善は、こうなります。
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <i className="ri-arrow-up-line text-xl text-white"></i>
                </div>
                <h3 className="font-bold text-gray-900">よくある改善</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-blue-500 mt-1"></i>
                  <span>人を増やす</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-blue-500 mt-1"></i>
                  <span>調剤スピードを上げる</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-blue-500 mt-1"></i>
                  <span>処理量を増やす</span>
                </li>
              </ul>
              <p className="mt-4 text-sm text-gray-600">すると一時的に人は忙しくなります。</p>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-orange-50 p-6 rounded-xl border border-red-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                  <i className="ri-arrow-down-line text-xl text-white"></i>
                </div>
                <h3 className="font-bold text-gray-900">しかし...</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-800">
                  <i className="ri-close-circle-fill text-red-500 mt-1"></i>
                  <span>WIP（仕掛品）が増える</span>
                </li>
                <li className="flex items-start gap-2 text-gray-800">
                  <i className="ri-close-circle-fill text-red-500 mt-1"></i>
                  <span>待ちが増える</span>
                </li>
                <li className="flex items-start gap-2 text-gray-800">
                  <i className="ri-close-circle-fill text-red-500 mt-1"></i>
                  <span>鑑査・投薬に負荷集中</span>
                </li>
                <li className="flex items-start gap-2 text-gray-800">
                  <i className="ri-close-circle-fill text-red-500 mt-1"></i>
                  <span>ミスリスク上昇</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-red-500">
            <p className="text-lg font-semibold text-gray-900">
              👉 全体のリードタイムは、むしろ伸びる
            </p>
            <p className="text-gray-700 mt-2">
              これが「改善しているのに現場が楽にならない」理由です。
            </p>
          </div>
        </div>
      </section>

      {/* Pharmacy Translation */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">調剤薬局での翻訳｜フロー効率の正体</h2>
        
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-8 rounded-2xl">
          <p className="text-lg text-gray-800 mb-6">薬局の1処方箋を例にします。</p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white p-6 rounded-xl">
              <div className="text-sm text-gray-600 mb-2">調剤・鑑査・投薬：合計</div>
              <div className="text-4xl font-bold text-emerald-600">10分</div>
            </div>
            <div className="bg-white p-6 rounded-xl">
              <div className="text-sm text-gray-600 mb-2">実際の受け取りまで</div>
              <div className="text-4xl font-bold text-teal-600">60分</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl mb-6">
            <p className="text-gray-700 mb-3">このときのフロー効率は、</p>
            <div className="text-center py-4">
              <div className="text-3xl font-bold text-gray-900 mb-2">
                10 ÷ 60 = <span className="text-emerald-600">約17％</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-xl border-l-4 border-orange-500">
            <p className="text-lg font-semibold text-gray-900 mb-2">
              つまり、<span className="text-orange-600">83％は"何も起きていない時間"</span>です。
            </p>
            <p className="text-gray-700">
              この83％を減らすことが、最もコスパの良い改善になります。
            </p>
          </div>
        </div>
      </section>

      {/* Three Typical Behaviors */}
      <section className="bg-gradient-to-br from-gray-50 to-slate-50 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">フロー効率を壊す3つの典型行動</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border-t-4 border-red-500">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-red-600">①</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">「作れるときに作る」</h3>
              <p className="text-gray-700 mb-3">調剤を先行→ 次工程が詰まり、棚待ちが増える</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border-t-4 border-orange-500">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-orange-600">②</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">「重い処方を後回し」</h3>
              <p className="text-gray-700 mb-3">一包化を最後に→ WIPの塊ができ、全体停止</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border-t-4 border-amber-500">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-amber-600">③</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">「全員を忙しくする」</h3>
              <p className="text-gray-700 mb-3">空いている人に仕事を振る→ 制約工程が埋もれる</p>
            </div>
          </div>

          <div className="mt-8 bg-white p-6 rounded-xl shadow-sm border-l-4 border-red-500">
            <p className="text-lg font-semibold text-gray-900">
              👉 忙しさが増えるほど、フローは悪化します。
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">フロー効率を上げると、何が起きるか</h2>
        
        <p className="text-lg text-gray-700 mb-8">
          フロー効率改善は、数字以上の効果があります。
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-xl border border-emerald-200">
            <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center mb-4">
              <i className="ri-time-line text-2xl text-white"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">待ち時間短縮</h3>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-200">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-4">
              <i className="ri-emotion-happy-line text-2xl text-white"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">現場ストレス低下</h3>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-xl border border-purple-200">
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mb-4">
              <i className="ri-focus-line text-2xl text-white"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">投薬時の集中力回復</h3>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-orange-50 p-6 rounded-xl border border-red-200">
            <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mb-4">
              <i className="ri-shield-check-line text-2xl text-white"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">誤薬リスク低下</h3>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-6 rounded-xl border border-amber-200">
            <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mb-4">
              <i className="ri-star-line text-2xl text-white"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">患者満足度向上</h3>
          </div>
        </div>

        <div className="mt-8 bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border-l-4 border-emerald-500">
          <p className="text-lg font-semibold text-gray-900">
            👉 安全・効率・体験が同時に改善します。
          </p>
        </div>
      </section>

      {/* Relationship with TOC/DBR */}
      <section className="bg-gradient-to-br from-gray-50 to-slate-50 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">TOC・DBRとの関係</h2>
          
          <p className="text-lg text-gray-700 mb-8">
            フロー効率は、TOC（制約理論）と完全に同じ思想圏です。
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-search-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">TOC</h3>
              <p className="text-gray-700">制約を見つける</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-stop-circle-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">DBR</h3>
              <p className="text-gray-700">入れすぎを止める</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border-2 border-emerald-500">
              <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-speed-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">フロー効率</h3>
              <p className="text-gray-700">待ちを減らす</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-emerald-500">
            <p className="text-lg font-semibold text-gray-900">
              👉 フロー効率は、「TOCが正しく機能しているか」を測る体温計です。
            </p>
          </div>
        </div>
      </section>

      {/* KPI */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">KPI｜フロー効率を測る指標</h2>
        
        <p className="text-lg text-gray-700 mb-8">難しい数式は不要です。</p>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-xl border border-emerald-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                <i className="ri-timer-line text-xl text-white"></i>
              </div>
              <h3 className="font-bold text-gray-900">受付〜投薬までの平均リードタイム</h3>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <i className="ri-time-line text-xl text-white"></i>
              </div>
              <h3 className="font-bold text-gray-900">実作業時間（目安でOK）</h3>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-xl border border-purple-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                <i className="ri-percent-line text-xl text-white"></i>
              </div>
              <h3 className="font-bold text-gray-900">フロー効率（％）</h3>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-6 rounded-xl border border-amber-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <i className="ri-pause-circle-line text-xl text-white"></i>
              </div>
              <h3 className="font-bold text-gray-900">工程間の滞留時間（どこで止まっているか）</h3>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white p-6 rounded-xl shadow-sm border-l-4 border-emerald-500">
          <p className="text-lg font-semibold text-gray-900">
            これだけで、「どこを触るべきか」が見えます。
          </p>
        </div>
      </section>

      {/* Tomorrow's Action */}
      <section className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white/95 backdrop-blur-sm p-8 rounded-2xl shadow-xl">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-lightbulb-flash-line text-2xl text-white"></i>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-3">明日の一手｜最初の改善ポイント</h2>
                <p className="text-lg text-gray-700 mb-4">明日やることは1つ。</p>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border-l-4 border-emerald-500">
              <p className="text-xl font-bold text-gray-900 mb-4">
                👉 「処方箋が"何分待っているだけか"を観察する」
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-emerald-500 mt-1"></i>
                  <span>どこで止まっているか</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-emerald-500 mt-1"></i>
                  <span>なぜ次に流れないか</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4 font-medium">
                ここを見始めた瞬間、改善の順番が逆転します。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Theories */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">関連する理論</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <Link 
            to="/theory/toc" 
            className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 hover:border-blue-500 cursor-pointer"
          >
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-4">
              <i className="ri-focus-line text-2xl text-white"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">TOC（制約理論）</h3>
            <p className="text-sm text-gray-600">制約を見つける</p>
          </Link>

          <Link 
            to="/theory/dbr" 
            className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 hover:border-emerald-500 cursor-pointer"
          >
            <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center mb-4">
              <i className="ri-git-repository-line text-2xl text-white"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">DBR</h3>
            <p className="text-sm text-gray-600">入れすぎを止める</p>
          </Link>

          <Link 
            to="/theory" 
            className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 hover:border-gray-500 cursor-pointer"
          >
            <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center mb-4">
              <i className="ri-arrow-left-line text-2xl text-white"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-2">理論一覧に戻る</h3>
            <p className="text-sm text-gray-600">すべての理論を見る</p>
          </Link>
        </div>
      </section>
    </div>
  );
}
